<template>
  <section class="bg-white p-6 border border-gray-200 rounded-md">
    <div class="flex items-center gap-2 mb-4">
      <i class="pi pi-wallet text-primary text-lg"></i>
      <h3 class="font-semibold text-lg text-gray-700">Bills</h3>
    </div>

    <div v-if="bills && bills.length">
      <div v-for="bill in bills" :key="bill.id" class="mb-6 border border-gray-300 rounded-lg p-4 bg-gray-50" data-testid="student-bill-card">
        <div class="flex justify-between items-start gap-2 mb-2">
          <div>
            <h4 class="font-semibold text-gray-700">{{ billTitle(bill) }}</h4>
            <p class="text-xs text-gray-500">{{ billedToLabel(bill) }}</p>
          </div>
          <Tag :value="billStatusLabel(bill.status, 'staff')" :severity="getStatusSeverity(bill.status)" />
        </div>
        <p class="text-sm text-gray-500 mb-2">
          Session: {{ bill.academicCalendar?.session?.name || bill.session?.name || '—' }} •
          {{ labels.TERM }}: {{ bill.academicCalendar?.termStructure?.name || '—' }}
        </p>
        <p class="text-sm mb-4">
          <strong>Due date:</strong> {{ formatDisplayDate(bill.due_date) }}
        </p>

        <div class="border-t border-gray-200 pt-2">
          <h5 class="font-semibold text-gray-700 mb-2">Fees</h5>
          <div v-for="detail in bill.billDetails" :key="detail.id"
            class="flex justify-between text-sm py-1 border-b border-gray-100 last:border-0">
            <span>{{ detail.item_name ?? detail.billItemTemplate?.name ?? 'Fee' }}</span>
            <span>{{ formatCurrency(detail.amount) }}</span>
          </div>

          <div class="flex justify-between mt-3 font-semibold text-gray-700">
            <span>Total before discounts:</span>
            <span>{{ formatCurrency(bill.amount) }}</span>
          </div>
        </div>
      </div>
      <p class="text-xs text-muted-color">
        Discounts, payments and the balance owed are on the student account.
      </p>
    </div>

    <div v-else class="text-gray-500 text-center py-4">
      No bills for this student yet.
    </div>

    <div v-if="studentId" class="mt-4 pt-4 border-t border-gray-200">
      <Button
        label="Open student account"
        icon="pi pi-external-link"
        severity="secondary"
        size="small"
        outlined
        data-testid="btn-open-student-account"
        @click="router.push({ name: routeNames.ADMIN_BILLING_STUDENT_FINANCIALS.name, params: { id: String(studentId) } })"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import type { Bill } from '@/types/composition-functions';
import { routeNames } from '@/constants/route-details.constant';
import { useSchoolLabels } from '@/composables/useSchoolLabels';
import { getStatusSeverity } from '@/utils/statusSeverity';
import { billStatusLabel } from '@/utils/billingLabels';
import { formatCurrency } from '@/utils/currency';
import { billTitle, billedToLabel, formatDisplayDate } from '@/utils/studentAccountFormat';

const router = useRouter();
const { labels } = useSchoolLabels();

defineProps<{
  bills: Bill[] | undefined
  studentId?: string | number
}>();
</script>
